import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// In-memory cache
const cache = {};
let siteConfigCache = null;

/**
 * Auto-injects SEO meta tags based on current route.
 * Uses React 19's native metadata rendering (title, meta, link tags in components).
 */
export default function AutoSEO() {
  const location = useLocation();
  const [seo, setSeo] = useState(null);
  const [siteConfig, setSiteConfig] = useState(siteConfigCache);
  const path = location.pathname;

  // Fetch site config for default OG image
  useEffect(() => {
    if (siteConfigCache) {
      setSiteConfig(siteConfigCache);
      return;
    }
    fetch(`${API}/api/site-config`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          siteConfigCache = data;
          setSiteConfig(data);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (path.startsWith('/admin')) return;

    const lookupPath = path.replace(/^\//, '') || '';

    if (cache[path]) {
      setSeo(cache[path]);
      return;
    }

    fetch(`${API}/api/page-seo/${lookupPath}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.title) {
          cache[path] = data;
          setSeo(data);
        }
      })
      .catch(() => {});
  }, [path]);

  useEffect(() => {
    if (path.startsWith('/admin')) return;

    // NotFoundPage sets this so AutoSEO defers to it. Without the guard,
    // AutoSEO would overwrite the 404 noindex / status-code meta with the
    // default site title + a self-canonical to the missing URL (bad SEO).
    if (typeof window !== 'undefined' && window.__ihgNotFound) return;

    // Helper to set/create meta tags
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Helper to set canonical link
    const setCanonical = (href) => {
      if (!href) return;
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Use page-specific SEO or defaults
    const title = seo?.title || 'IHGIND | Architectural Metal Fabrication Dubai';
    const description = seo?.description || '';
    const keywords = Array.isArray(seo?.keywords) ? seo.keywords.join(', ') : (seo?.keywords || '');
    const ogTitle = seo?.og_title || seo?.title || title;
    const ogDesc = seo?.og_description || description;
    // Use page-specific og_image, or site config og_image as fallback
    const ogImage = seo?.og_image || siteConfig?.og_image || '';
    const canonical = seo?.canonical_url || `https://ihgind.com${path}`;

    // Update document title
    document.title = title;

    // Standard meta tags
    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);

    // Open Graph
    setMeta('property', 'og:title', ogTitle);
    setMeta('property', 'og:description', ogDesc);
    if (ogImage) {
      // Ensure absolute URL for OG image
      const absoluteOgImage = ogImage.startsWith('http') ? ogImage : `https://ihgind.com${ogImage}`;
      setMeta('property', 'og:image', absoluteOgImage);
      setMeta('property', 'og:image:secure_url', absoluteOgImage);
      setMeta('name', 'twitter:image', absoluteOgImage);
    }
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'IHG Industry');

    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', ogTitle);
    setMeta('name', 'twitter:description', ogDesc);

    // Canonical
    setCanonical(canonical);

    // Robots
    if (seo?.no_index) {
      setMeta('name', 'robots', 'noindex, nofollow');
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"][content="noindex, nofollow"]');
      if (robotsMeta) robotsMeta.remove();
    }
  }, [seo, siteConfig, path]);

  return null; // No JSX needed — all DOM manipulation
}
